/**
 * EventRow — the single, shared event-log row: the in-game log
 * (RecentActivitySidebar) and chat log lines (ChatLogRow). Previously each
 * screen had its own near-identical row + colour-dot, which drifted. One
 * component, two size variants:
 *   - `full`    — larger text, roomy padding (the native-regressions preview).
 *   - `compact` — the in-game log (smaller text, tighter rows).
 *
 * Renders the visual only; callers wrap it for tap-to-view (they own the
 * absolute event index they navigate to).
 */
import React from 'react'
import { ChatFlowBody } from './ChatFlowBody'
import { View, Text, TouchableOpacity } from 'react-native'
import type { GamePlayerResponse } from '@warcarp/api-types'
import type { LogRow } from '../../lib/logFeed'
import { formatTimeAgo } from '../../lib/gameLog'
import { dotColorsForRow, highlightsForRow } from '../../lib/renderLogText'
import { FormattedLogText } from './FormattedLogText'
import { useTheme } from '../../hooks/useTheme'
import { logNameColor } from '../../lib/playerTextColor'

/** Player colour dot, sized to align with the row's first text line. */
export function PlayerDot({ color, compact = false, testID }: { color: string; compact?: boolean; testID?: string }) {
  const line = compact ? 16 : 20
  const dot = compact ? 6 : 8
  return (
    <View testID={testID} style={{ height: line, justifyContent: 'center', flexShrink: 0 }}>
      <View style={{ width: dot, height: dot, borderRadius: dot / 2, backgroundColor: color }} />
    </View>
  )
}

export interface EventLogRowProps {
  /** An event row — or a chat message dressed as one (see lib/logFeed). */
  row: LogRow
  playerBySlot: Map<string, GamePlayerResponse>
  isSelected?: boolean
  /** Tap-to-view — receives the row's absolute event index. Omit for read-only. */
  onPress?: (eventIndex: number) => void
  variant?: 'full' | 'compact'
  /** Shown just left of the time-ago (chat rows: their reaction chips). */
  trailing?: React.ReactNode
  reactionCounts?: number[]
}

export const EventLogRow = React.memo(function EventLogRow({
  row,
  playerBySlot,
  isSelected,
  onPress,
  variant = 'full',
  trailing,
  reactionCounts = [],
}: EventLogRowProps) {
  const compact = variant === 'compact'
  const { theme } = useTheme()
  const ink = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const softInk = theme === 'dark' ? '#8a8c6e' : '#6b5b3e'
  const blood = theme === 'dark' ? '#e0463f' : '#a01818'
  const diceColor = row.isGameEnd ? blood : row.isTurnStart || !compact ? ink : softInk
  const rowPadding = row.chat ? compact ? 'py-2' : 'py-3' : compact ? 'py-1.5' : 'py-2.5'
  const boardColors = dotColorsForRow(row, playerBySlot)
  // Per-theme name colours: hand-tuned overrides first, else the generic
  // rule (dark lightens along the hue against the log surface; light uses
  // the board colour). See lib/playerTextColor LOG_NAME_TINT.
  const logColor = (color: string) => logNameColor(color, theme)
  const actorColor = boardColors.actor ? logColor(boardColors.actor) : null
  const defenderColor = boardColors.defender ? logColor(boardColors.defender) : null

  const inner = (
    <View
      className={`${compact ? 'px-3' : 'px-4'} ${rowPadding} ${
        row.isGameEnd ? 'bg-blood/10 dark:bg-[#1a0a00]' : row.isTurnStart ? 'bg-paper-up' : ''
      } ${isSelected ? 'border-l-4 border-blood' : ''}`}
    >
      <View className={`flex-row ${compact ? 'gap-1.5' : 'gap-2'} items-start`}>
        {!row.chat && actorColor && <PlayerDot color={actorColor} compact={compact} />}
        {defenderColor && <PlayerDot color={defenderColor} compact={compact} />}
        {row.chat ? <ChatFlowBody content={row.chat.content} authorName={row.chat.authorName}
          leading={actorColor ? <PlayerDot color={actorColor} compact={compact} testID="chat-player-dot" /> : undefined}
          timestamp={formatTimeAgo(row.ts)} reactions={trailing} counts={reactionCounts} compact={compact} /> : <>
        <View className="flex-1">
          <FormattedLogText
              text={row.primary}
              diceSize={compact ? 26 : 28}
              fontSize={compact ? 12 : 14}
              color={diceColor}
              highlights={highlightsForRow(row, playerBySlot).map(item => ({ ...item, color: logColor(item.color) }))}
              className={`font-typed ${compact ? 'text-xs leading-4' : 'text-sm leading-5'} ${
                row.isGameEnd
                  ? 'text-blood'
                  : row.isTurnStart
                    ? 'text-ink font-semibold'
                    : compact
                      ? 'text-ink-soft'
                      : 'text-ink'
              }`}
              numberOfLines={compact ? 3 : undefined}
          />
          {row.sub.map((line, idx) => (
            <FormattedLogText
              key={idx}
              text={line}
              diceSize={compact ? 24 : 18}
              fontSize={compact ? 10 : 12}
              color={softInk}
              className={`font-typed ${compact ? 'text-[10px]' : 'text-xs'} text-ink-soft mt-0.5 pl-2`}
              numberOfLines={compact ? 1 : undefined}
            />
          ))}
        </View>
        {trailing}
        <Text
          className={`font-typed ${
            compact ? 'text-[10px] leading-4 text-ink-soft/60' : 'text-[11px] text-ink-soft/70 leading-5'
          }`}
          numberOfLines={1}
        >
          {formatTimeAgo(row.ts)}
        </Text>
        </>}
      </View>
    </View>
  )

  if (!onPress) return inner
  return (
    <TouchableOpacity
      onPress={() => onPress(row.eventIndex)}
      // Chat rows open the message in the in-game drawer; their DISTINCT
      // label also keeps them out of the e2e /^View map at:/ selector.
      accessibilityLabel={
        row.chat ? `View in chat: ${row.primary}` : `View map at: ${row.primary}`
      }
      accessibilityRole="button"
    >
      {inner}
    </TouchableOpacity>
  )
})
